<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="installation-address-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.installationAddress.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="installation-address-page-title"
        />
      </div>

      <div data-cy="installation-address-form-address-input">
        <MoleculeLabel
          :text="t('subscription.installationAddress.form.address.label')"
          label-for="address"
          class="mb-1 mt-6"
        />
        <MoleculeInputText
          v-model="address"
          id="address"
          name="address"
          class="w-full"
          :placeholder="t('subscription.installationAddress.form.address.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          :input-state="isSubmitted && getInputState({ errorMessage: errors.address })"
          :helper="errors.address && getHelperMessage({ errorMessage: errors.address })"
        />
      </div>

      <div data-cy="installation-address-form-address-complement-input">
        <MoleculeLabel
          :text="t('subscription.installationAddress.form.addressComplement.label')"
          :optional-text="t('common.forms.optional')"
          label-for="addressComplement"
          class="mt-6 mb-1"
        />
        <MoleculeInputText
          v-model="addressComplement"
          id="addressComplement"
          name="addressComplement"
          class="w-full"
          :placeholder="t('subscription.installationAddress.form.addressComplement.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          :input-state="isSubmitted && getInputState({ errorMessage: errors.addressComplement })"
          :helper="errors.address && getHelperMessage({ errorMessage: errors.addressComplement })"
        />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div data-cy="installation-address-form-zip-code-input">
          <MoleculeLabel
            :text="t('subscription.installationAddress.form.zipCode.label')"
            label-for="zipCode"
            class="mt-6 mb-1"
          />
          <MoleculeInputText
            v-model="zipCode"
            id="zipCode"
            name="zipCode"
            class="w-full"
            :placeholder="t('subscription.installationAddress.form.zipCode.placeholder')"
            :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
            :input-state="isSubmitted && getInputState({ errorMessage: errors.zipCode })"
            :helper="errors.zipCode && getHelperMessage({ errorMessage: errors.zipCode })"
          />
        </div>
        <div data-cy="installation-address-form-city-input">
          <MoleculeLabel
            :text="t('subscription.installationAddress.form.city.label')"
            label-for="city"
            class="mt-6 mb-1"
          />
          <MoleculeInputText
            v-model="city"
            name="city"
            id="city"
            class="w-full"
            :placeholder="t('subscription.installationAddress.form.city.placeholder')"
            :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
            :input-state="isSubmitted && getInputState({ errorMessage: errors.city })"
            :helper="errors.city && getHelperMessage({ errorMessage: errors.city })"
          />
        </div>
      </div>

      <div
        v-if="isActiveContractQuestionDisplayed"
        class="mt-6"
        data-cy="installation-address-form-active-contract"
      >
        <MoleculeLabel
          :text="t('subscription.installationAddress.form.activeContract.label')"
          label-for="hasDeclaredActiveContractWithAnotherSupplier"
          class="mb-1"
        />
        <AtomText
          :text="t('subscription.installationAddress.form.activeContract.question')"
          font-size="text-sm"
          class="mb-2"
          data-cy="installation-address-form-active-contract-question"
        />
        <OrganismRadioGroupCard
          v-model="hasDeclaredActiveContractWithAnotherSupplier"
          :cards="activeContractChoices"
          name="hasDeclaredActiveContractWithAnotherSupplier"
          orientation="horizontal"
          :error-message="isSubmitted ? errors.hasDeclaredActiveContractWithAnotherSupplier : ''"
          :has-error="isSubmitted && !!errors.hasDeclaredActiveContractWithAnotherSupplier"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          data-cy="installation-address-form-active-contract-radio-group"
        />
      </div>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useLoadMaps } from '@/composables/useLoadMaps.composable';
import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { RgvRadioGroupCard } from '@/types/regenerativ.type';
import { getIsPreloadedBatteryOfferEnabled } from '@/utils/env.util';
import type { GoogleAddress } from '@/utils/geocoderAddress.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import * as Sentry from '@sentry/vue';

const { t } = useI18n();
const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { getInputState, getHelperMessage } = useInputState();

const { loadMaps, initAddressAutocomplete } = useLoadMaps();

const { goToNextStep } = stepStore;

const {
  isGetSubscriptionInProgress,
  isGetSubscriptionSuccessfully,
  isSaveSubscriptionInProgress,
  getCurrentSubscriptionInstallationAddress,
  getCustomerDeclarations,
} = storeToRefs(subscriptionStore);

const isActiveContractQuestionDisplayed = getIsPreloadedBatteryOfferEnabled();

const activeContractChoices: RgvRadioGroupCard[] = [
  {
    id: 'has-active-contract-yes',
    title: t('subscription.installationAddress.form.activeContract.options.yes'),
    value: true,
  },
  {
    id: 'has-active-contract-no',
    title: t('subscription.installationAddress.form.activeContract.options.no'),
    value: false,
  },
];

const installationAddressSchema = yup.object({
  address: yup.string().required(t('common.forms.required')),
  addressComplement: yup.string(),
  zipCode: yup.string().required(t('common.forms.required')),
  city: yup.string().required(t('common.forms.required')),
  hasDeclaredActiveContractWithAnotherSupplier: yup
    .boolean()
    .nullable()
    .test(
      'required-when-displayed',
      t('common.forms.required'),
      value => !isActiveContractQuestionDisplayed || value !== null
    ),
});

const {
  errors,
  defineField,
  validate: validateInstallationAddress,
} = useForm({
  validationSchema: installationAddressSchema,
  initialValues: {
    address: getCurrentSubscriptionInstallationAddress.value?.address || '',
    addressComplement: getCurrentSubscriptionInstallationAddress.value?.addressComplement || '',
    zipCode: getCurrentSubscriptionInstallationAddress.value?.zipCode || '',
    city: getCurrentSubscriptionInstallationAddress.value?.city || '',
    hasDeclaredActiveContractWithAnotherSupplier:
      getCustomerDeclarations.value?.hasDeclaredActiveContractWithAnotherSupplier ?? null,
  },
});

const [address] = defineField('address');
const [addressComplement] = defineField('addressComplement');
const [zipCode] = defineField('zipCode');
const [city] = defineField('city');
const [hasDeclaredActiveContractWithAnotherSupplier] = defineField(
  'hasDeclaredActiveContractWithAnotherSupplier'
);

const isSubmitted = ref(false);

const onSubmit = async () => {
  isSubmitted.value = true;

  toastSnackBar.clearToasts();

  const installationAddressValidation = await validateInstallationAddress();

  if (!installationAddressValidation.valid) {
    return;
  }

  try {
    await subscriptionStore.updateSubscriptionWithInstallationAddress({
      address: address.value,
      addressComplement: addressComplement.value,
      zipCode: zipCode.value,
      city: city.value,
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: 'installation-address',
        action: 'updateSubscriptionWithInstallationAddress',
      },
    });
    toastSnackBar.addErrorToast({
      id: 'installation-address-error',
      title: t('common.apiError'),
      content: t(
        'subscription.installationAddress.api.errors.patchSubscriptionInstallationAddress'
      ),
    });
    return;
  }

  if (isActiveContractQuestionDisplayed) {
    try {
      await subscriptionStore.updateSubscriptionWithCustomerDeclarations({
        hasDeclaredActiveContractWithAnotherSupplier:
          hasDeclaredActiveContractWithAnotherSupplier.value as boolean,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: 'installation-address',
          action: 'updateSubscriptionWithCustomerDeclarations',
        },
      });
      toastSnackBar.addErrorToast({
        id: 'installation-address-customer-declarations-error',
        title: t('common.apiError'),
        content: t(
          'subscription.installationAddress.api.errors.patchSubscriptionCustomerDeclarations'
        ),
      });
      return;
    }
  }

  goToNextStep();
};

const setAddressFromGoogleApi = (fullAddress: GoogleAddress) => {
  address.value = fullAddress.address;
  zipCode.value = fullAddress.postalCode;
  city.value = fullAddress.city;
};

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    address.value = getCurrentSubscriptionInstallationAddress.value?.address || '';
    addressComplement.value =
      getCurrentSubscriptionInstallationAddress.value?.addressComplement || '';
    zipCode.value = getCurrentSubscriptionInstallationAddress.value?.zipCode || '';
    city.value = getCurrentSubscriptionInstallationAddress.value?.city || '';
    hasDeclaredActiveContractWithAnotherSupplier.value =
      getCustomerDeclarations.value?.hasDeclaredActiveContractWithAnotherSupplier ?? null;
  }
});

onMounted(async () => {
  await loadMaps(['places']);
  initAddressAutocomplete('address', setAddressFromGoogleApi);
});
</script>

<style scoped></style>
